/*
  variant-picker-orphan-killer.js
  -------------------------------------------------------------
  Kills "ghost" / orphan option-values in the variant picker.

  Problem:
  Shopify keeps option values in product.options_with_values[*].values
  even after all variants using them have been deleted. The Impact theme
  variant-picker then renders them with the .is-disabled class (diagonal
  strike-through). They have no real variant behind them — clicking them
  does nothing useful.

  Fix:
  After render (and on every mutation), compare every option-value element
  in the picker against the actual product.variants JSON. If no variant
  matches the combination of currently-selected option values + this
  option-value's own position, remove the DOM element entirely.

  Robust against:
  - Re-renders after qty change, section rendering API swaps
  - Quick-buy modal, sticky ATC, multiple pickers on one page
  - Both "product has 1 option" and "product has 2+ options"
  - Limited-edition split fieldsets
*/
(() => {
  const PICKER_SEL = 'variant-picker';
  const VARIANTS_SEL = 'script[data-variants][type="application/json"]';

  const parse = (el) => {
    try { return JSON.parse(el.textContent || '[]'); } catch { return []; }
  };

  const getVariants = (picker) => {
    const script = picker.querySelector(VARIANTS_SEL);
    return script ? parse(script) : [];
  };

  // Which option-values are "valid" (have at least one real variant)
  // for a given option position, considering the currently selected values
  // of the OTHER options?
  const computeValidValues = (picker, variants) => {
    // Read current selection per position from DOM
    const selected = { 1: null, 2: null, 3: null };
    picker.querySelectorAll('input[type="radio"][data-option-position]:checked')
      .forEach((inp) => {
        const pos = parseInt(inp.getAttribute('data-option-position'), 10);
        if (!pos) return;
        // Prefer data-option-value, fall back to label text
        let name = inp.getAttribute('data-option-value');
        if (!name && inp.id) {
          const lab = picker.querySelector(`label[for="${CSS.escape(inp.id)}"]`);
          if (lab) name = (lab.textContent || '').trim();
        }
        if (!name) name = inp.value || null;
        selected[pos] = name;
      });

    // For each position, build the set of valid values
    const valid = { 1: new Set(), 2: new Set(), 3: new Set() };

    // Position 1: value is valid if there's any variant with option1 == v
    //             (Option1 must never be filtered by other selections —
    //              matches the SSR logic in variant-picker.liquid.)
    variants.forEach((v) => {
      if (v.option1) valid[1].add(v.option1);
    });

    // Position 2: value is valid if there's any variant where
    //             option1 == selected[1] (if set) AND option2 == v
    variants.forEach((v) => {
      if (selected[1] && v.option1 !== selected[1]) return;
      if (v.option2) valid[2].add(v.option2);
    });

    // Position 3: value is valid if option1 & option2 match the selection
    variants.forEach((v) => {
      if (selected[1] && v.option1 !== selected[1]) return;
      if (selected[2] && v.option2 !== selected[2]) return;
      if (v.option3) valid[3].add(v.option3);
    });

    return { selected, valid };
  };

  // Return the wrapper element to remove for a given radio input.
  // Walks up to the closest .vp-item (custom wrapper) or .variant-picker__option-values > *
  const getWrapper = (el) => {
    return el.closest('.vp-item')
      || el.closest('.variant-picker__option-values > *')
      || el.closest('label')
      || el;
  };

  // Check whether a given combination of option values corresponds to an
  // actually existing variant.
  const comboExists = (variants, selected) =>
    variants.some((v) =>
      (selected[1] ? v.option1 === selected[1] : true) &&
      (selected[2] ? v.option2 === selected[2] : true) &&
      (selected[3] ? v.option3 === selected[3] : true)
    );

  // Pick the best fallback variant when the current selection is invalid.
  // Strategy: keep position 1 (Menge) fixed, find first available variant
  // for that position. Fall back to any available if still nothing.
  const pickFallback = (variants, selected) => {
    // 1) Prefer variants with same option1 (Menge) + available
    if (selected[1]) {
      const sameQtyAvailable = variants.find(
        (v) => v.option1 === selected[1] && v.available
      );
      if (sameQtyAvailable) return sameQtyAvailable;

      const sameQty = variants.find((v) => v.option1 === selected[1]);
      if (sameQty) return sameQty;
    }
    // 2) Any available variant
    const anyAvailable = variants.find((v) => v.available);
    if (anyAvailable) return anyAvailable;
    // 3) Anything
    return variants[0] || null;
  };

  // Click the radio matching the given option value (position + name).
  const selectRadio = (picker, pos, name) => {
    if (!name) return false;
    const radios = picker.querySelectorAll(
      `input[type="radio"][data-option-position="${pos}"]`
    );
    for (const r of radios) {
      // Check data-option-value first
      let rname = r.getAttribute('data-option-value');
      if (!rname && r.id) {
        const lab = picker.querySelector(`label[for="${CSS.escape(r.id)}"]`);
        if (lab) rname = (lab.textContent || '').trim();
      }
      if (!rname) rname = r.value || '';
      if (rname === name) {
        if (r.checked) return true;
        if (r.disabled) continue;
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  };

  const cleanPicker = (picker) => {
    if (picker.dataset.orphanKillerLock === '1') return;
    const variants = getVariants(picker);
    if (!variants.length) return;

    picker.dataset.orphanKillerLock = '1';

    try {
      const { selected, valid } = computeValidValues(picker, variants);

      // Walk every radio input in the picker
      const radios = picker.querySelectorAll('input[type="radio"][data-option-position]');
      const removed = new Set();

      radios.forEach((inp) => {
        const pos = parseInt(inp.getAttribute('data-option-position'), 10);
        if (!pos || !valid[pos]) return;

        // Resolve the value name
        let name = inp.getAttribute('data-option-value');
        if (!name && inp.id) {
          const lab = picker.querySelector(`label[for="${CSS.escape(inp.id)}"]`);
          if (lab) name = (lab.textContent || '').trim();
        }
        if (!name) name = inp.value || '';

        if (!name) return;

        // Is this a valid (existing) value?
        if (valid[pos].has(name)) return;

        // Orphan → nuke the whole wrapper
        const wrap = getWrapper(inp);
        if (wrap && !removed.has(wrap)) {
          removed.add(wrap);
          wrap.remove();
        }
      });

      // After removal: if any fieldset is now empty, hide it too
      picker.querySelectorAll('fieldset.variant-picker__option').forEach((fs) => {
        const hasAny = fs.querySelector('input[type="radio"]');
        if (!hasAny) fs.style.display = 'none';
      });

      // AUTO-FALLBACK: if the currently selected combination of option
      // values does NOT map to a real variant (e.g. user switched Menge
      // to one where the current color doesn't exist), force-select the
      // best alternative.
      if (!comboExists(variants, selected)) {
        const fallback = pickFallback(variants, selected);
        if (fallback) {
          // Only change option2/3 — keep Menge (option1) fixed since
          // the user just explicitly clicked it.
          let changed = false;
          if (fallback.option2 && fallback.option2 !== selected[2]) {
            if (selectRadio(picker, 2, fallback.option2)) changed = true;
          }
          if (fallback.option3 && fallback.option3 !== selected[3]) {
            if (selectRadio(picker, 3, fallback.option3)) changed = true;
          }
          // If option2/3 didn't yield a match, and option1 fallback
          // differs, accept Menge change as last resort.
          if (!changed && fallback.option1 && fallback.option1 !== selected[1]) {
            selectRadio(picker, 1, fallback.option1);
          }
        }
      }
    } finally {
      requestAnimationFrame(() => { delete picker.dataset.orphanKillerLock; });
    }
  };

  const cleanAll = () => {
    document.querySelectorAll(PICKER_SEL).forEach(cleanPicker);
  };

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanAll, { once: true });
  } else {
    cleanAll();
  }

  // Re-run on any DOM change (quick-buy, section rendering API, etc.)
  let scheduled = false;
  const mo = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      cleanAll();
    });
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Re-run on any picker change (user selects another option)
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.type !== 'radio') return;
    const picker = t.closest(PICKER_SEL);
    if (!picker) return;
    requestAnimationFrame(() => cleanPicker(picker));
  });
})();